import { Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { usePageTitle } from "@/hooks/usePageTitle";
import PageHero from "@/components/PageHero";
import CTABand from "@/components/CTABand";
import DownloadButton from "@/components/DownloadButton";
import ProtectedDownload from "@/components/ProtectedDownload";
import { Reveal } from "@/components/Reveal";
import { FlowerDoodle, WavyUnderline, Bee } from "@/components/Doodles";

const FAQS = [
    {
        q: "What ages do you care for?",
        a: "Little ones across our three groups — Seedlings (infants), Sprouts (toddlers), and Blooms (preschool). Call or text Asia for the current age ranges and openings.",
    },
    {
        q: "What should I pack?",
        a: "2 to 3 labeled changes of clothes, diapers, wipes, and cream if needed, and a small blanket and one comfort item for rest time. No toys, candy, or money.",
    },
    { q: "Are meals included?", a: "Yes: breakfast, lunch, and afternoon snack." },
    {
        q: "When should my child stay home sick?",
        a: "Fever of 100.4°F or higher with symptoms, vomiting or diarrhea twice in 24 hours, or a rash that hasn't been checked. They can return after 24 hours symptom-free.",
    },
    {
        q: "Do you give medicine?",
        a: "We don't give oral medications. With written permission we apply sunscreen, creams, and bug spray. Emergency medicines like EpiPens and inhalers are allowed with a health care plan.",
    },
    { q: "Can I visit during the day?", a: "Yes, any time. We have an open door policy." },
    {
        q: "Do you go outside?",
        a: "Every day, weather permitting. Please dress your child for mess and weather.",
    },
    {
        q: "Do you take photos?",
        a: "Only with your written permission, and we never post a child's full name.",
    },
    {
        q: "Do you drive the children anywhere?",
        a: "No. Neighborhood walks happen only with your permission.",
    },
    {
        q: "Do you accept childcare assistance?",
        a: "Yes, Nassau County DSS. Families pay any co-pay or uncovered amount.",
    },
];

const DOWNLOADS = [
    { label: "Parent Handbook", id: "parent-handbook", protected: true },
    { label: "Enrollment Packet", id: "enrollment-packet", protected: true },
    { label: "Closure Calendar", id: "closure-calendar" },
    { label: "Sample Menu", id: "weekly-menu" },
];

export default function Parents() {
    usePageTitle("Parent Corner & FAQs | Little Seeds Flourish Daycare");

    return (
        <div>
            <PageHero
                eyebrow="Parent corner"
                title={<>Quick Answers, Whenever You Need Them</>}
                sub="The questions parents ask most — answered straight from our Parent Handbook."
                doodle="bee"
            />

            <section className="pb-16 md:pb-20">
                <div className="max-w-3xl mx-auto px-5 sm:px-8">
                    <Accordion type="single" collapsible className="space-y-3" data-testid="parents-faq-accordion">
                        {FAQS.map((f, i) => (
                            <AccordionItem
                                key={f.q}
                                value={`faq-${i}`}
                                className="bg-white border-2 border-soil/10 rounded-3xl px-5 border-b-2 last:border-b-2"
                            >
                                <AccordionTrigger
                                    data-testid={`faq-trigger-${i + 1}`}
                                    className="font-display font-semibold text-lg text-left hover:no-underline [&>svg]:text-sageink [&>svg]:w-6 [&>svg]:h-6 py-4"
                                >
                                    {f.q}
                                </AccordionTrigger>
                                <AccordionContent data-testid={`faq-answer-${i + 1}`} className="text-soilmuted leading-relaxed pb-5">
                                    {f.a}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>

                    <Reveal>
                        <div className="mt-12 text-center" data-testid="parents-downloads">
                            <h2 className="font-display font-semibold text-2xl md:text-3xl mb-2">
                                Downloads
                                <WavyUnderline className="block w-36 h-2.5 mt-2 mx-auto" color="#F6C544" />
                            </h2>
                            <p className="text-soilmuted mb-6">Take the essentials with you.</p>
                            <div className="flex flex-wrap justify-center gap-3">
                                {DOWNLOADS.map((d) =>
                                    d.protected ? (
                                        <ProtectedDownload key={d.id} file={`${d.id}.pdf`} label={d.label} testId={`parents-download-${d.id}`} />
                                    ) : (
                                        <DownloadButton key={d.id} href={`/docs/${d.id}.pdf`} label={d.label} testId={`parents-download-${d.id}`} />
                                    ),
                                )}
                            </div>
                            <p className="mt-4 text-xs text-soil/45">
                                The Handbook and Packet are password-protected for enrolled & touring families — Asia shares the
                                password after your tour. The calendar and menu are open to everyone.
                            </p>
                        </div>
                    </Reveal>

                    <Reveal delay={0.1}>
                        <div className="mt-12 bg-sage/10 border-2 border-sage/25 rounded-[2rem] p-7 text-center relative overflow-hidden" data-testid="parents-forms-cta">
                            <Bee className="absolute top-4 right-5 opacity-80" size={38} />
                            <h2 className="font-display font-semibold text-xl md:text-2xl mb-2">Getting ready for day one?</h2>
                            <p className="text-soilmuted mb-4">
                                Every form your child needs — with friendly checklists and who-signs-what tags.
                            </p>
                            <Link
                                to="/forms"
                                data-testid="parents-forms-link"
                                className="inline-flex items-center gap-2 bg-sage hover:bg-sagedeep text-white font-extrabold px-7 py-3.5 rounded-full transition-colors"
                            >
                                View Enrollment Forms <FlowerDoodle size={17} petals="#FFF8EC" />
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </section>

            <CTABand title="Still have questions? Ask us in person." />
        </div>
    );
}
